import { all, fork, put, select, takeLatest } from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  HEARINGTYPE_TYPE,
  INVITATIONGROUP_TYPE,
  INVITATIONGROUPMAPPING_TYPE,
} from '../../constants/api';
import {
  addInvitationGroupMappingsToStore,
  addInvitationKeysToStore,
  createInvitationGroupInitialize,
  deleteInvitationGroupInitialize,
  getAllInvitationGroupsInitialize,
  removeInvitationGroupMappingsFromStore,
  removeInvitationKeysFromStore,
  updateInvitationGroupInitialize,
  updateInvitationGroupMappingsInitialize,
  updateInvitationKeysInitialize,
} from '../actions/api';
import { awaitApiCall } from '../helpers/sagaHelpers';
import { parseApiError } from '../globalMessageSlice';
import { invitationGroupMappingIdsByHearingTypeId } from '../apiFeatures/invitationGroupMappingSlice';
import { invitationGroupByIdSelector } from '../apiFeatures/invitationGroupSlice';
import { invitationKeyIdsByInvitationGroupId } from '../apiFeatures/invitationKeySlice';

// ---------- ACTIONS -----------
const setIsUpdateInvitationGroupMappingsPanelOpen = createAction(
  'page/invitationGroupAdministration/setIsUpdateInvitationGroupMappingsPanelOpen',
);
const setIsCreateInvitationGroupPanelOpen = createAction(
  'page/invitationGroupAdministration/setIsCreateInvitationGroupPanelOpen',
);
const setIsUpdateInvitationGroupPanelOpen = createAction(
  'page/invitationGroupAdministration/setIsUpdateInvitationGroupPanelOpen',
);
const setIsDeleteInvitationGroupPanelOpen = createAction(
  'page/invitationGroupAdministration/setIsDeleteInvitationGroupPanelOpen',
);
const setIsManageInvitationKeysPanelOpen = createAction(
  'page/invitationGroupAdministration/setIsManageInvitationKeysPanelOpen',
);
const setIsUpdatingInvitationKeys = createAction(
  'page/invitationGroupAdministration/setIsUpdatingInvitationKeys',
);
const updateInvitationGroupsAndHearingType = createAction(
  'page/invitationGroupAdministration/updateInvitationGroupAndHearingType',
);
const createInvitationGroup = createAction(
  'page/invitationGroupAdministration/createInvitationGroup',
);
const updateInvitationGroup = createAction(
  'page/invitationGroupAdministration/updateInvitationGroup',
);
const deleteInvitationGroup = createAction(
  'page/invitationGroupAdministration/deleteInvitationGroup',
);
const updateInvitationKeys = createAction(
  'page/invitationGroupAdministration/updateInvitationKeys',
);

// ---------- REDUCERS -----------
const invitationGroupAdministrationPageReducer = createReducer(
  {
    isUpdateInvitationGroupMappingsPanelOpen: false,
    isCreateInvitaionGroupPanelOpen: false,
    isUpdateInvitationGroupPanelOpen: false,
    isDeleteInvitationGroupPanelOpen: false,
    isManageInvitationKeysPanelOpen: false,
    isUpdatingInvitationKeys: false,
  },
  (builder) => {
    builder
      .addCase(setIsUpdateInvitationGroupMappingsPanelOpen, (state, action) => {
        state.isUpdateInvitationGroupMappingsPanelOpen = action.payload;
      })
      .addCase(setIsCreateInvitationGroupPanelOpen, (state, action) => {
        state.isCreateInvitaionGroupPanelOpen = action.payload;
      })
      .addCase(setIsUpdateInvitationGroupPanelOpen, (state, action) => {
        state.isUpdateInvitationGroupPanelOpen = action.payload;
      })
      .addCase(setIsDeleteInvitationGroupPanelOpen, (state, action) => {
        state.isDeleteInvitationGroupPanelOpen = action.payload;
      })
      .addCase(setIsManageInvitationKeysPanelOpen, (state, action) => {
        state.isManageInvitationKeysPanelOpen = action.payload;
      })
      .addCase(setIsUpdatingInvitationKeys, (state, action) => {
        state.isUpdatingInvitationKeys = action.payload;
      });
  },
);

// ----------- Sagas -------------
export function* invitationGroupAdministrationPageSaga() {
  yield all([
    fork(createInvitationGroupSaga),
    fork(updateInvitationGroupSaga),
    fork(deleteInvitationGroupSaga),
    fork(updateInvitationGroupAndHearingTypeSaga),
    fork(updateInvitationKeysSaga),
  ]);
}

function* createInvitationGroupSaga() {
  yield takeLatest(createInvitationGroup.type, function* (action) {
    const title = action.payload.title;

    const requestData = {
      type: INVITATIONGROUP_TYPE,
      attributes: {
        name: title,
      },
      relationships: {},
    };

    const initAction = yield put(
      createInvitationGroupInitialize({ data: requestData }),
    );

    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.error) {
      yield put(parseApiError(actionResult.error.payload));
    }
    yield put(setIsCreateInvitationGroupPanelOpen(false));
  });
}

function* updateInvitationGroupSaga() {
  yield takeLatest(updateInvitationGroup.type, function* (action) {
    const { title, invitationGroupId } = action.payload;

    const invitationGroup = yield select(
      invitationGroupByIdSelector(invitationGroupId),
    );

    const requestData = Object.assign({}, invitationGroup, {
      attributes: { ...invitationGroup.attributes, name: title },
      relationships: { ...invitationGroup.relationships },
    });

    const initAction = yield put(
      updateInvitationGroupInitialize({
        invitationGroupId,
        changes: requestData,
      }),
    );

    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.error) {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(setIsUpdateInvitationGroupPanelOpen(false));
  });
}

function* deleteInvitationGroupSaga() {
  yield takeLatest(deleteInvitationGroup.type, function* (action) {
    const invitationGroupId = action.payload;

    const initAction = yield put(
      deleteInvitationGroupInitialize(invitationGroupId),
    );

    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.error) {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(setIsDeleteInvitationGroupPanelOpen(false));
  });
}

function* updateInvitationGroupAndHearingTypeSaga() {
  yield takeLatest(updateInvitationGroupsAndHearingType.type, function* (
    action,
  ) {
    const {
      selectedHearingTypeKey,
      selectedInvitationGroupIds,
    } = action.payload;

    // Keep track of which ID's to remove from the Redux store
    const invitationGroupMappingIdsToRemove = yield select(
      invitationGroupMappingIdsByHearingTypeId(selectedHearingTypeKey),
    );

    const requestData = selectedInvitationGroupIds.map((invitationGroupId) => {
      return {
        data: {
          type: INVITATIONGROUPMAPPING_TYPE,
          attributes: {},
          relationships: {
            [INVITATIONGROUP_TYPE]: {
              data: {
                type: INVITATIONGROUP_TYPE,
                id: invitationGroupId,
              },
            },
            [HEARINGTYPE_TYPE]: {
              data: {
                type: HEARINGTYPE_TYPE,
                id: selectedHearingTypeKey,
              },
            },
          },
        },
      };
    });

    const initAction = yield put(
      updateInvitationGroupMappingsInitialize({
        hearingTypeId: selectedHearingTypeKey,
        dtos: requestData,
      }),
    );

    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      yield put(
        removeInvitationGroupMappingsFromStore(
          invitationGroupMappingIdsToRemove,
        ),
      );
      yield put(
        addInvitationGroupMappingsToStore(actionResult.success.payload),
      );
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(setIsUpdateInvitationGroupMappingsPanelOpen(false));
  });
}

function* updateInvitationKeysSaga() {
  yield takeLatest(updateInvitationKeys.type, function* (action) {
    const { invitationGroupId, invitationKeys } = action.payload;

    yield put(setIsUpdatingInvitationKeys(true));

    // Keep track of which ID's to remove from the Redux store
    const invitationKeyIdsToRemove = yield select(
      invitationKeyIdsByInvitationGroupId(invitationGroupId),
    );

    const requestData = invitationKeys.map((invitationKey) => {
      return {
        data: {
          ...invitationKey,
          attributes: {
            ...invitationKey.attributes,
          },
          relationships: {
            ...invitationKey.relationships,
            [INVITATIONGROUP_TYPE]: {
              data: {
                type: INVITATIONGROUP_TYPE,
                id: invitationGroupId,
              },
            },
          },
        },
      };
    });

    const initAction = yield put(
      updateInvitationKeysInitialize({
        invitationGroupId: invitationGroupId,
        dtos: requestData,
      }),
    );

    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      yield put(removeInvitationKeysFromStore(invitationKeyIdsToRemove));
      yield put(addInvitationKeysToStore(actionResult.success.payload));
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }

    // Fetch invitationGroups to get the updated InvitationGroup relationships
    yield put(getAllInvitationGroupsInitialize());
    yield put(setIsManageInvitationKeysPanelOpen(false));
    yield put(setIsUpdatingInvitationKeys(false));
  });
}

// ----------- Selectors -----------

const invitationGroupAdministrationState = (state) =>
  state.page.invitationGroupAdministration;

const isUpdateInvitationGroupMappingsPanelOpenSelector = createSelector(
  invitationGroupAdministrationState,
  (state) => state.isUpdateInvitationGroupMappingsPanelOpen,
);

const isCreateInvitationGroupPanelOpenSelector = createSelector(
  invitationGroupAdministrationState,
  (state) => state.isCreateInvitaionGroupPanelOpen,
);

const isUpdateInvitationGroupPanelOpenSelector = createSelector(
  invitationGroupAdministrationState,
  (state) => state.isUpdateInvitationGroupPanelOpen,
);

const isDeleteInvitationGroupPanelOpenSelector = createSelector(
  invitationGroupAdministrationState,
  (state) => state.isDeleteInvitationGroupPanelOpen,
);

const isManageInvitationKeysPanelOpenSelector = createSelector(
  invitationGroupAdministrationState,
  (state) => state.isManageInvitationKeysPanelOpen,
);

const isUpdatingInvitationKeysSelector = createSelector(
  invitationGroupAdministrationState,
  (state) => state.isUpdatingInvitationKeys,
);

export {
  isUpdateInvitationGroupMappingsPanelOpenSelector,
  isCreateInvitationGroupPanelOpenSelector,
  isUpdateInvitationGroupPanelOpenSelector,
  isDeleteInvitationGroupPanelOpenSelector,
  isManageInvitationKeysPanelOpenSelector,
  isUpdatingInvitationKeysSelector,
  setIsUpdateInvitationGroupMappingsPanelOpen,
  setIsCreateInvitationGroupPanelOpen,
  setIsUpdateInvitationGroupPanelOpen,
  setIsDeleteInvitationGroupPanelOpen,
  setIsManageInvitationKeysPanelOpen,
  updateInvitationGroupsAndHearingType,
  createInvitationGroup,
  updateInvitationGroup,
  deleteInvitationGroup,
  updateInvitationKeys,
};

export default invitationGroupAdministrationPageReducer;
